import { getDbOrThrow } from "../../core/firestoreCore";
import { COLLECTIONS, userScopedId, slugify } from "../../core/collections";
import {
  collection,
  doc,
  serverTimestamp,
  setDoc,
  updateDoc,
  getDoc,
  writeBatch,
  increment,              // ⬅️ NEW
} from "firebase/firestore";
import { isDryRunFlag, logDryRunBanner, coerceUser } from "../../core/writerUtils";

/* ------------------------------------------------------------------ */
/* Canonical shaper: Accounts                                         */
/* ------------------------------------------------------------------ */
export function shapeAccount({ account, mappedType }) {
  if (!account) return null;

  const slug = (account.slug || slugify(account.name || "")).toLowerCase();
  const specials =
    (Array.isArray(mappedType?.special_attributes) && mappedType.special_attributes) ||
    (Array.isArray(mappedType?.specialAttributes) && mappedType.specialAttributes) ||
    [];

  return {
    name: account.name,
    slug,
    currencyCode: account.currencyCode ?? null,
    accountBalance: account.accountBalance ?? account.balance ?? 0,
    ...(mappedType
      ? {
        systemTypeId: mappedType.id ?? null,
        systemTypeName: mappedType.name ?? null,
        systemTypeDesc: mappedType.desc ?? null,
      }
      : {}),
    ...(specials.length ? { specialAttributes: specials } : {}),
    source: account.source || "csv-upload",
  };
}

/* ------------------------------------------------------------------ */
/* Single-record helpers (existing)                                   */
/* ------------------------------------------------------------------ */

export async function createUserAccount(payload, { dryRun } = {}) {
  const db = getDbOrThrow();

  const {
    user: _ignoredUser,
    userId: _ignoredUserId,
    userEmail: _ignoredUserEmail,

    name,
    currencyCode = null,
    systemTypeId,
    systemTypeName,
    systemTypeDesc,
    balance,
    alertThreshold,
    ...rest
  } = payload || {};

  const { uid, email } = coerceUser(payload);
  if (!name) throw new Error("createUserAccount: name is required");
  if (!systemTypeId) throw new Error("createUserAccount: systemTypeId is required");

  const s = slugify(name);
  const id = userScopedId(uid, s);
  const ref = doc(collection(db, COLLECTIONS.userAccounts), id);

  const DRY = isDryRunFlag(dryRun);
  const now = serverTimestamp();

  const record = {
    ...rest,
    userId: uid,
    userEmail: email,
    name,
    slug: s,
    currencyCode,
    systemTypeId,
    systemTypeName: systemTypeName ?? systemTypeId,
    systemTypeDesc: systemTypeDesc ?? "",
    ...(balance !== undefined ? { accountBalance: balance } : {}),
    ...(alertThreshold !== undefined ? { alertThreshold } : {}),
    source: "ui-manual",
    createdAt: now,
    updatedAt: now,
  };

  if (DRY) {
    logDryRunBanner("createUserAccount", { id, name, currencyCode, systemTypeId });
  } else {
    const existing = await getDoc(ref);
    if (existing.exists()) throw new Error(`Account "${name}" already exists.`);
    await setDoc(ref, record, { merge: true });
  }

  return { id, ...record, __dryRun: DRY };
}

export async function updateUserAccount({ id, updates }, { dryRun } = {}) {
  const db = getDbOrThrow();
  if (!id) throw new Error("updateUserAccount: missing id");

  const DRY = isDryRunFlag(dryRun);
  const FORBIDDEN_KEYS = new Set(["user", "userId", "userEmail", "createdAt"]);

  // Build the base patch (mapping balance -> accountBalance if present),
  // but we'll strip it later if we're doing the synthetic transaction path.
  const base = {
    ...updates,
    ...(updates && Object.prototype.hasOwnProperty.call(updates, "balance")
      ? { accountBalance: updates.balance }
      : {}),
    updatedAt: serverTimestamp(),
  };
  const patch = Object.fromEntries(
    Object.entries(base).filter(([k, v]) => !FORBIDDEN_KEYS.has(k) && v !== undefined)
  );

  const ref = doc(collection(db, COLLECTIONS.userAccounts), id);
  const prevSnap = await getDoc(ref);
  if (!prevSnap.exists()) throw new Error("updateUserAccount: account not found");

  const prev = prevSnap.data() || {};
  const userLike = { uid: prev.userId, email: prev.userEmail };

  const hasBalanceEdit =
    Object.prototype.hasOwnProperty.call(updates || {}, "balance") ||
    Object.prototype.hasOwnProperty.call(updates || {}, "accountBalance");

  // If balance was edited, compute Δ = new - old
  let delta = 0;
  if (hasBalanceEdit) {
    const nextBal = parseAmountValueLoose(
      Object.prototype.hasOwnProperty.call(updates || {}, "balance")
        ? updates.balance
        : updates.accountBalance
    );
    const prevBal = parseAmountValueLoose(prev.accountBalance ?? 0);
    if (!Number.isFinite(nextBal) || !Number.isFinite(prevBal)) {
      throw new Error("updateUserAccount: invalid balance value");
    }
    delta = Math.round((nextBal - prevBal) * 100) / 100;
    // We do NOT directly set accountBalance; it will be moved by the synthetic tx.
    delete patch.accountBalance;
    delete patch.balance;
  }

  if (DRY) {
    // Dry-run logging mirrors actual behavior
    const keys = Object.keys(patch);
    logDryRunBanner("updateUserAccount", {
      id,
      keys,
      ...(hasBalanceEdit ? { delta } : {}),
    });
    return true;
  }

  const now = serverTimestamp();
  const batch = writeBatch(db);

  // Always apply other account field edits (name, currency, type, etc.)
  if (Object.keys(patch).length) {
    batch.set(ref, patch, { merge: true });
  } else {
    // Still bump updatedAt even if only balance changed;
    batch.set(ref, { updatedAt: now }, { merge: true });
  }

  if (hasBalanceEdit && delta !== 0) {
    // 1) create synthetic Balance Adjustment transaction
    const txRef = doc(collection(db, COLLECTIONS.userTransactions));
    const txPayload = {
      id: txRef.id,
      userId: userLike.uid,
      userEmail: String(userLike.email || "").toLowerCase(),
      accountId: id,
      accountName: updates?.name ?? prev?.name ?? "",   // ← add this
      account: updates?.name ?? prev?.name ?? "",        // ← add alias if your table uses "account"
      typeName: "Balance Adjustments",
      amount: delta,
      currencyCode: prev.currencyCode ?? null,
      date: todayISO(),
      description: "System (Account) Balance Adjustment",
      descriptionLower: "system_account_balance_adjustment",
      tagPath: ["Balance"],
      source: "system",
      createdAt: now,
      updatedAt: now,
    };
    
    batch.set(txRef, txPayload, { merge: true });

    // 2) increment the account balance by Δ atomically with the tx write
    batch.set(ref, { accountBalance: increment(delta), updatedAt: now }, { merge: true });
  }

  await batch.commit();
  return true;
}


function parseAmountValueLoose(v) {
  if (typeof v === "number") return v;
  if (v == null) return NaN;
  let s = String(v).trim();
  let neg = false;
  if (s.startsWith("(") && s.endsWith(")")) { neg = true; s = s.slice(1, -1); }
  s = s.replace(/[^\d.,-]+/g, "");
  if (s.includes(",") && s.includes(".")) s = s.replace(/,/g, "");
  else if (s.includes(",") && !s.includes(".")) s = s.replace(/,/g, ".");
  const n = parseFloat(s);
  return Number.isNaN(n) ? NaN : (neg ? -n : n);
}
function todayISO() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const Day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${Day}`;
}

/* ------------------------------------------------------------------ */
/* Optional batch upsert used by CSV pipeline                         */
/* ------------------------------------------------------------------ */
export async function upsertAccountsBatch({ user, accounts = [], accountMapping = {}, status, dryRun }) {
  if (!user?.uid) throw new Error("Not signed in.");
  const db = getDbOrThrow();
  const { uid, email } = coerceUser({ user });
  const DRY = isDryRunFlag(dryRun);
  const now = serverTimestamp();
  const BATCH = 400;

  for (let i = 0; i < accounts.length; i += BATCH) {
    const b = writeBatch(db);
    const slice = accounts.slice(i, i + BATCH);
    for (const acc of slice) {
      const shaped = shapeAccount({ account: acc, mappedType: accountMapping[acc.slug] });
      if (!shaped) continue;
      const id = userScopedId(uid, shaped.slug);
      const ref = doc(collection(db, COLLECTIONS.userAccounts), id);
      const payload = { ...shaped, userId: uid, userEmail: email, createdAt: now, updatedAt: now };
      if (!DRY) b.set(ref, payload, { merge: true });
    }
    if (!DRY) await b.commit();
    status?.(`Accounts ${Math.min(i + BATCH, accounts.length)} / ${accounts.length}`);
  }
}

/* ------------------------------------------------------------------ */
/* NEW: centralized balance adjustment                                */
/* ------------------------------------------------------------------ */

/**
 * Apply net balance deltas to accounts.
 * Accepts one of:
 *  - Map<accountId, number>
 *  - Array<{ accountId: string, delta: number }>
 *  - Plain object { [accountId]: number }
 */
export async function applyAccountBalanceDeltas({ user, deltas, dryRun } = {}) {
  const db = getDbOrThrow();
  if (!user?.uid) throw new Error("Not signed in.");

  // Normalize input into a Map
  const net = new Map();
  const add = (aid, d) => {
    const id = String(aid || "").trim();
    // const n = Number(d);
    const n = Math.round(Number(d) * 100) / 100;
    if (!id || !Number.isFinite(n) || n === 0) return;
    net.set(id, (net.get(id) || 0) + n);
  };

  if (deltas instanceof Map) {
    for (const [aid, d] of deltas.entries()) add(aid, d);
  } else if (Array.isArray(deltas)) {
    for (const it of deltas) add(it?.accountId, it?.delta);
  } else if (deltas && typeof deltas === "object") {
    for (const [aid, d] of Object.entries(deltas)) add(aid, d);
  }

  if (net.size === 0) return { applied: 0, __dryRun: !!dryRun };

  const DRY = isDryRunFlag(dryRun);
  const now = serverTimestamp();
  const batch = writeBatch(db);
  let count = 0;

  for (const [accountId, delta] of net.entries()) {
    const aref = doc(collection(db, COLLECTIONS.userAccounts), accountId);
    if (!DRY) batch.set(aref, { accountBalance: increment(delta), updatedAt: now }, { merge: true });
    count++;
  }

  if (!DRY) await batch.commit();
  return { applied: count, __dryRun: DRY };
}
